import type { User } from '~/server/types';
import { PermissionEnum } from 'generated/graphql';
import type { ContentItemType } from '~/modules/root/util/item-link-helpers';
import {
    getContentEditorLink,
    getRepositionThumbLink,
} from '~/modules/root/util/item-link-helpers';
import { getEditPermission } from '~/modules/user/hook/use-permissions';
import type { EditableItem } from '../type';
import type { ProfessionalTileFragment } from 'generated/graphql';

/**
 * Maps ContentItemType to the appropriate PermissionEnum for edit permissions.
 */
function getPermissionForItemType(itemType: ContentItemType): PermissionEnum {
    switch (itemType) {
        case 'articles':
            return PermissionEnum.BmArticleEdit;
        case 'projects':
            return PermissionEnum.BmProjectEdit;
        case 'products':
            return PermissionEnum.BmProductsEdit;
        case 'professionals':
            return PermissionEnum.BmProfileEdit;
        default:
            return PermissionEnum.BmProfileEdit;
    }
}

interface EditableItemProps {
    itemType: ContentItemType;
    link: string;
    professionalID: number;
    professionalLink: string;
    user?: User;
}

const getEditableItem = (props: EditableItemProps): EditableItem => {
    const permission = getPermissionForItemType(props.itemType);
    return {
        canEdit: props.user
            ? getEditPermission(props.user, props.professionalID, permission)
            : false,
        bmEditorLink: getContentEditorLink(
            props.itemType,
            props.link,
            props.professionalLink,
        ),
        repositionThumbLink: getRepositionThumbLink(
            props.itemType,
            props.link,
            props.professionalLink,
        ),
    };
};

/**
 * Checks if the user is a staff admin (ArchiPro staff with admin privileges).
 * This mirrors the PHP isAdmin() logic in ArchiproMemberExtension.php.
 */
function isUserStaffAdmin(user?: User): boolean {
    if (!user || user.__typename !== 'Me') {
        return false;
    }
    return user.IsStaffAdmin === true;
}

export const getTileEditableItems = ({
    professional,
    user,
    itemType,
    itemLink,
    requireStaffAdmin = false,
}: {
    professional?: Pick<ProfessionalTileFragment, 'ID' | 'Link'> | null;
    user?: User;
    itemType: ContentItemType;
    itemLink: string;
    /** If true, only staff admins can edit (used for featured tiles). */
    requireStaffAdmin?: boolean;
}) => {
    if (!professional?.ID || !user) {
        return undefined;
    }

    const bmEditorLink = getContentEditorLink(
        itemType,
        itemLink,
        professional.Link || '',
    );
    const repositionThumbLink = getRepositionThumbLink(
        itemType,
        itemLink,
        professional.Link || '',
    );

    // For featured tiles, restrict editing to staff admins only
    if (requireStaffAdmin) {
        const isStaffAdmin = isUserStaffAdmin(user);

        return {
            canEdit: isStaffAdmin,
            bmEditorLink,
            repositionThumbLink,
        };
    }

    return getEditableItem({
        itemType: itemType,
        link: itemLink,
        professionalID: professional.ID,
        professionalLink: professional.Link || '',
        user,
    });
};

interface VisitBMEditorOptions {
    repositionThumb?: boolean;
}

export const visitBMEditor = (
    item: EditableItem,
    sameTab?: boolean,
    options?: VisitBMEditorOptions,
) => {
    // Navigate to bm editor on the old website.
    if (window && item.bmEditorLink) {
        let bmLink = item.bmEditorLink;
        if (options?.repositionThumb && item.repositionThumbLink) {
            bmLink = item.repositionThumbLink;
        }

        window.open(bmLink, sameTab ? '_parent' : '_blank');
    }
};
